import { StateCreator } from "zustand";
import { FieldProblemSource } from "./field-problems.types";
import {
	formatArticle,
	tariffToJobItem,
	calculateTariffPrice,
	getJobPricingProblems,
	jobPricingProblemsMessage,
} from "./utils";
import { Logger } from "@/lib/logger/Logger";
import { useCentralStore } from "../Central";
import { supabase } from "@/lib/supabase";
import {
	JobItemEntityType,
	JobItemEntityTypeInsert,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { produce } from "immer";
import { v4 as uuidv4 } from "uuid";
import { showNotification } from "../Central/selectors";
import { ExtendedArticleEntityType } from "./types";
import { JobStoreUnion } from ".";
import { JobStatusEnum } from "@/lib/types/job";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { TariffWithPricesViewType, useCatalogsStore } from "../Catalogs";

interface JobItemState {
	jobItemsForDocuments: Record<string, JobItemEntityType[]>;
	timers: Record<number, NodeJS.Timeout>;
	defaultTechnician: string | null;
	itemSelectionDialog: {
		open: boolean;
		items: (TariffWithPricesViewType | ExtendedArticleEntityType)[];
		code: string;
		itemType: "article" | "tariff";
		jobDocumentId: number;
		resolve:
			| ((
					item:
						| TariffWithPricesViewType
						| ExtendedArticleEntityType
						| null
			  ) => void)
			| null;
	} | null;
}

export const initialJobItemState: JobItemState = {
	timers: {},
	jobItemsForDocuments: {},
	defaultTechnician: null,
	itemSelectionDialog: null,
};

export interface JobItemSlice extends JobItemState {
	fetchJobItems: (jobDocumentId: number) => Promise<void>;
	setJobItemsForDocument: (
		jobDocumentId: number,
		jobItems: JobItemEntityType[]
	) => void;
	setDefaultTechnician: (profileId: string | null) => void;
	/**
	 * Persists a new manual order for a job document's items. `orderedIds` is the full
	 * list of item ids in the desired order; every item's `position` is rewritten to its
	 * index so the order survives to the PDF. Also used to bake a column sort into the
	 * stored order.
	 */
	reorderJobItems: (
		jobDocumentId: number,
		orderedIds: string[]
	) => Promise<void>;
	/**
	 * Single point to add a job item to a job document (tariff or article)
	 */
	addJobItem: (
		identifier: { code_e: string; id: string | null },
		jobDocumentId: number,
		itemType: "article" | "tariff",
		options?: { force: boolean }
	) => Promise<void>;
	/**
	 * When a user types in a code, we need to find the job item by the code; sometimes resulting in multiple job items found
	 */
	_addJobItemByCode: (
		code: string,
		jobDocumentId: number,
		itemType: "article" | "tariff"
	) => Promise<void>;
	/**
	 * When a user selects a tariff or article from the list, we can find the job item by the id directly
	 */
	_addJobItemById: (
		id: string,
		code_e: string,
		jobDocumentId: number,
		itemType: "article" | "tariff"
	) => Promise<void>;
	_processFoundJobItems: (
		itemsToAdd: (TariffWithPricesViewType | ExtendedArticleEntityType)[],
		itemType: "article" | "tariff",
		jobDocumentId: number
	) => Promise<void>;
	/**
	 * Returns the job's TP-Wert and Preisliste, or flags the missing ones on the job page,
	 * toasts once and returns null.
	 */
	requireJobPricing: (
		source: Extract<
			FieldProblemSource,
			"item-insert" | "price-recalculation" | "template-document"
		>
	) => { tpValue: number; priceListId: string } | null;
	deleteJobItems: (
		jobItemIds: string[],
		jobDocumentId: number
	) => Promise<void>;
	pasteJobItems: (
		jobItems: JobItemEntityType[],
		jobDocumentId: number,
		forceDuplicate?: boolean
	) => Promise<void>;
	changeJobItem: (jobItem: any, field?: string) => Promise<void>;
	bulkChangeJobItems: (
		jobItems: JobItemEntityType[],
		jobDocumentId: number
	) => Promise<void>;
	upsertJobItems: (
		jobDocumentId: number,
		errorMessage: string,
		isHistoryOperation?: boolean,
		skipRefetch?: boolean
	) => Promise<void>;
	handleTimer: (
		jobDocumentId: number,
		itemType: "tariff" | "article" | "group"
	) => Promise<void>;
	//updateJobItems: (jobId: number, tp_value?: number, tp_variation?: number | null, price_list_id?: string) => Promise<void>;
	updateJobItems: (
		jobId: number,
		tp_value?: number,
		tp_variation?: number
	) => Promise<void>;
	changeTariffInRow: (
		code: string,
		jobItem: JobItemEntityType
	) => Promise<{ error: boolean; message: string }>;
	handleItemSelection: (
		item: TariffWithPricesViewType | ExtendedArticleEntityType | null
	) => void;
}

export const createJobItemStore: StateCreator<
	JobStoreUnion,
	[],
	[],
	JobItemSlice
> = (set, get) => ({
	...initialJobItemState,
	fetchJobItems: async (jobDocumentId: number) => {
		if (!jobDocumentId) {
			Logger.error(
				"fetchJobItems called with null/undefined jobDocumentId"
			);
			return;
		}

		const { data, error } = await supabase
			.from(SupabaseTableEnum.JOB_ITEMS)
			.select(`*`)
			.eq("job_document_id", jobDocumentId)
			.order("position", { ascending: true, nullsFirst: false })
			.order("code", { ascending: true });

		if (error) {
			showNotification({
				message: "Fehler beim Laden der Positionen",
				type: "error",
			});
			Logger.error(error);
			return;
		}

		set({
			jobItemsForDocuments: {
				...get().jobItemsForDocuments,
				[jobDocumentId]: data,
			},
		});
	},

	setJobItemsForDocument: (
		jobDocumentId: number,
		jobItems: JobItemEntityType[]
	) => {
		set(
			produce((state) => {
				state.jobItemsForDocuments[jobDocumentId] = jobItems;
			})
		);
	},
	setDefaultTechnician: (profileId: string | null) => {
		set(
			produce((state) => {
				state.defaultTechnician = profileId;
			})
		);
	},

	reorderJobItems: async (jobDocumentId: number, orderedIds: string[]) => {
		const current = get().jobItemsForDocuments[jobDocumentId];
		if (!current?.length) return;

		// Build the new order from orderedIds, then append any stored item the caller
		// missed (preserving its current order) so we always persist a complete, gap-free
		// position sequence instead of silently bailing.
		const byId = new Map(current.map((item) => [item.id, item]));
		const seen = new Set<string>();
		const ordered: JobItemEntityType[] = [];
		orderedIds.forEach((id) => {
			const item = byId.get(id);
			if (item && !seen.has(id)) {
				seen.add(id);
				ordered.push(item);
			}
		});
		current.forEach((item) => {
			if (!seen.has(item.id)) ordered.push(item);
		});

		if (ordered.length !== current.length) {
			Logger.error(
				"reorderJobItems: could not reconcile the order with the stored job items"
			);
			return;
		}

		const reordered = ordered.map((item, index) => ({
			...item,
			position: index,
		}));

		get().setJobItemsForDocument(jobDocumentId, reordered);
		await get().upsertJobItems(
			jobDocumentId,
			"Fehler beim Speichern der Reihenfolge"
		);
	},

	// Post since this is explicitly for updating the job items through the network and not the local store
	upsertJobItems: async (
		jobDocumentId: number,
		errorMessage: string,
		isHistoryOperation?: boolean,
		skipRefetch?: boolean
	) => {
		const previousJobItems = [...get().jobItemsForDocuments[jobDocumentId]];
		const items = get().jobItemsForDocuments[jobDocumentId].map((i) => {
			const {
				// @ts-expect-error unregistered properties
				interpr_de,
				// @ts-expect-error unregistered properties
				interpr_fr,
				// @ts-expect-error unregistered properties
				interpr_it,
				fpo_id,
				// @ts-expect-error unregistered properties
				bpo_id,
				...rest
			} = i;
			return {
				...rest,
			};
		});

		const { error } = await supabase
			.from(SupabaseTableEnum.JOB_ITEMS)
			.upsert(items, { onConflict: "id", defaultToNull: false });

		if (error) {
			showNotification({
				message: "Fehler beim Speichern der Positionen",
				type: "error",
			});
			Logger.error(
				`update db error: ${error}, errorMessage: ${errorMessage}`
			);

			if (!skipRefetch) {
				get().fetchJobItems(jobDocumentId);
			}

			return;
		}

		if (!isHistoryOperation) {
			get().pushToHistory(jobDocumentId, previousJobItems);
		}
	},
	addJobItem: async (
		identifier: { code_e: string; id: string | null },
		jobDocumentId: number,
		itemType: "article" | "tariff",
		options?: { force: boolean }
	) => {
		const existingItems: number[] = [];
		get().jobItemsForDocuments[jobDocumentId]?.forEach((item, i) => {
			if (
				item.code_e?.toLowerCase() ===
					identifier.code_e.toLowerCase() &&
				((itemType == "article" &&
					item.type == JobItemTypeEnum.ARTICLE_MATERIAL) ||
					(itemType == "tariff" &&
						item.type == JobItemTypeEnum.TARIFF))
			) {
				existingItems.push(i);
			}
		});

		if (existingItems.length > 0 && !options?.force) {
			set(
				produce((state) => {
					existingItems.forEach((i) => {
						state.jobItemsForDocuments[jobDocumentId][i].quantity +=
							1;
					});
				})
			);

			get().handleTimer(jobDocumentId, itemType);
			get().pendingChangesNotification();
			return;
		}

		// If id is provided, use id-based lookup for efficiency
		if (identifier.id !== null && identifier.id !== undefined) {
			await get()._addJobItemById(
				identifier.id,
				identifier.code_e,
				jobDocumentId,
				itemType
			);
		} else {
			// Otherwise use code_e lookup
			await get()._addJobItemByCode(
				identifier.code_e,
				jobDocumentId,
				itemType
			);
		}
	},
	_addJobItemByCode: async (
		code: string,
		jobDocumentId: number,
		itemType: "article" | "tariff"
	) => {
		let itemsToAdd: (
			| TariffWithPricesViewType
			| ExtendedArticleEntityType
		)[] = [];

		// Check if tariffs are still loading
		if (itemType == "tariff") {
			const catalogsState = useCatalogsStore.getState();
			if (catalogsState.isLoadingTariffs) {
				showNotification({
					message:
						"Leistungen werden noch geladen. Bitte versuchen Sie es in wenigen Sekunden erneut.",
					type: "info",
				});
				return;
			}
			const tariffsLookup = catalogsState.tariffsLookup;

			// First try exact match
			if (tariffsLookup[code]) {
				itemsToAdd = Object.values(tariffsLookup[code]);
			} else {
				// Try case insensitive search
				const matchingCodeE = Object.keys(tariffsLookup).find(
					(key) => key.toLowerCase() === code.toLowerCase()
				);
				if (matchingCodeE) {
					itemsToAdd = Object.values(tariffsLookup[matchingCodeE]);
				}
			}
		} else if (itemType == "article") {
			const articles = useCentralStore.getState().articles;

			// First try exact match
			if (articles[code]) {
				itemsToAdd = Object.values(articles[code]);
			} else {
				// Try case insensitive search
				const matchingCodeE = Object.keys(articles).find(
					(key) => key.toLowerCase() === code.toLowerCase()
				);
				if (matchingCodeE) {
					itemsToAdd = Object.values(articles[matchingCodeE]);
				}
			}
		}

		if (itemsToAdd.length === 0) {
			showNotification({
				message: "Position nicht gefunden.",
				type: "error",
			});
			return;
		}

		// Show dialog if multiple items found
		if (itemsToAdd.length > 1) {
			if (
				itemsToAdd.length === 2 &&
				itemsToAdd.some(
					(item) => (item as TariffWithPricesViewType)?.is_custom
				) &&
				itemsToAdd.some(
					(item) => !(item as TariffWithPricesViewType)?.is_custom
				) &&
				new Set(
					itemsToAdd.map(
						(item) =>
							(item as TariffWithPricesViewType)?.category_id
					)
				).size === 1
			) {
				itemsToAdd = itemsToAdd.filter(
					(item) => (item as TariffWithPricesViewType)?.is_custom
				);
			} else {
				const selectedItem = await new Promise<
					TariffWithPricesViewType | ExtendedArticleEntityType | null
				>((resolve) => {
					set({
						itemSelectionDialog: {
							open: true,
							items: itemsToAdd,
							code,
							itemType,
							jobDocumentId,
							resolve,
						},
					});
				});

				if (!selectedItem) {
					return;
				}

				itemsToAdd = [selectedItem];
			}
		}

		await get()._processFoundJobItems(itemsToAdd, itemType, jobDocumentId);
	},
	_addJobItemById: async (
		id: string,
		code_e: string,
		jobDocumentId: number,
		itemType: "article" | "tariff"
	) => {
		let item:
			| TariffWithPricesViewType
			| ExtendedArticleEntityType
			| undefined;

		if (itemType === "tariff") {
			const { tariffsLookup } = useCatalogsStore.getState();
			// Direct lookup using code_e
			if (tariffsLookup[code_e] && tariffsLookup[code_e][id]) {
				item = tariffsLookup[code_e][id];
			}
		} else {
			const { articles } = useCentralStore.getState();
			// Direct lookup using code_e
			if (articles[code_e] && articles[code_e][id]) {
				item = articles[code_e][id];
			}
		}

		if (!item) {
			showNotification({
				message: `Position nicht gefunden: Code ${code_e}, ID ${id}`,
				type: "error",
			});
			return;
		}

		await get()._processFoundJobItems([item], itemType, jobDocumentId);
	},
	requireJobPricing: (source) => {
		const job = get().job;
		if (!job) return null;
		const problems = getJobPricingProblems(job);
		get().setFieldProblems(job.id, source, problems);
		if (job.tp_value && job.price_list_id) {
			return { tpValue: job.tp_value, priceListId: job.price_list_id };
		}
		showNotification({
			message: jobPricingProblemsMessage(problems),
			type: "error",
		});
		return null;
	},
	_processFoundJobItems: async (
		itemsToAdd: (TariffWithPricesViewType | ExtendedArticleEntityType)[],
		itemType: "article" | "tariff",
		jobDocumentId: number
	) => {
		const job = get().job;
		if (!job) {
			showNotification({
				message:
					"Position kann nicht hinzugefügt werden. Fehlender Auftrag.",
				type: "error",
			});
			Logger.error("Job is not set");
			return;
		}
		const pricing = get().requireJobPricing("item-insert");
		if (!pricing) return;
		const { tpValue: jobTpValue, priceListId: jobPriceListId } = pricing;
		const jobTpVariation = job.tp_variation;

		const defaultTechnicianId = get().defaultTechnician;
		const technicianAppliesToArticles =
			(
				useCentralStore.getState().organization?.config as
					| Record<string, unknown>
					| null
					| undefined
			)?.technician_applies_to_articles === true;

		const formattedItems: JobItemEntityTypeInsert[] = [];

		for (const item of itemsToAdd) {
			const result =
				itemType === "tariff"
					? tariffToJobItem(
							item as TariffWithPricesViewType,
							jobDocumentId,
							jobPriceListId,
							jobTpValue,
							jobTpVariation ?? 0
						)
					: formatArticle(
							item as ExtendedArticleEntityType,
							jobDocumentId
						);
			Logger.info(`[addItem] formattedItem:`, result);

			const formattedItem = result.data;

			if (!formattedItem || result.error) {
				showNotification({
					message: `Position kann nicht hinzugefügt werden. Bei dieser Position fehlen Stammdaten: ${item.code_e}`,
					type: "error",
				});
				Logger.warn(`Could not format item with code ${item.code_e}`);
				continue;
			}
			if (formattedItem.price === null) {
				showNotification({
					message: `Position kann nicht hinzugefügt werden. Bei dieser Position fehlt der Preis: ${item.code_e}`,
					type: "error",
				});
				Logger.warn(`Item with code ${item.code_e} has no price`);
				continue;
			}

			if (
				defaultTechnicianId &&
				(itemType === "tariff" || technicianAppliesToArticles)
			) {
				formattedItem.profile_id =
					formattedItem.profile_id ?? defaultTechnicianId;
			}

			formattedItems.push(formattedItem);
		}

		if (formattedItems.length === 0) {
			Logger.warn(`No formatted items found`);
			return;
		}

		set(
			produce((state) => {
				if (!state.jobItemsForDocuments[jobDocumentId]) {
					state.jobItemsForDocuments[jobDocumentId] = [];
				}
				state.jobItemsForDocuments[jobDocumentId].push(
					...formattedItems
				);
			})
		);

		get().handleTimer(jobDocumentId, itemType);
		get().pendingChangesNotification();
	},
	handleTimer: async (
		jobDocumentId: number,
		itemType: "tariff" | "article" | "group"
	) => {
		const timer = get().timers[jobDocumentId];
		if (timer) clearTimeout(timer);

		const newTimer = setTimeout(async () => {
			Logger.info(`Adding ${itemType} to database`);
			get().upsertJobItems(jobDocumentId, `Error adding ${itemType}`);
			set(
				produce((state) => {
					delete state.timers[jobDocumentId];
				})
			);
		}, 3000);

		set(
			produce((state) => {
				state.timers[jobDocumentId] = newTimer;
			})
		);
	},
	deleteJobItems: async (jobItemIds: string[], jobDocumentId: number) => {
		const jobItemsForDocument = get().jobItemsForDocuments[jobDocumentId];
		const previousJobItems = [...jobItemsForDocument];
		const updatedJobItemsForDocument = jobItemsForDocument.filter(
			(item) => !jobItemIds.includes(item.id)
		);
		set(
			produce((state) => {
				state.jobItemsForDocuments[jobDocumentId] =
					updatedJobItemsForDocument;
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOB_ITEMS)
			.delete()
			.in("id", jobItemIds);

		if (error) {
			showNotification({
				message: "Fehler beim Löschen der Positionen",
				type: "error",
			});
			Logger.error(error);
			set(
				produce((state) => {
					state.jobItemsForDocuments[jobDocumentId] =
						previousJobItems;
				})
			);
			return;
		}

		get().pushToHistory(jobDocumentId, previousJobItems);
		showNotification({
			message: "Gelöscht",
			type: "success",
		});
	},

	pasteJobItems: async (
		jobItems: JobItemEntityType[],
		jobDocumentId: number,
		forceDuplicate: boolean = false
	) => {
		const existingItems = get().jobItemsForDocuments[jobDocumentId] || [];

		const newJobItems: JobItemEntityType[] = [];
		const itemsToUpdate: { id: string; quantityToAdd: number }[] = [];
		const matchedIds = new Set<string>(); // Track which existing items to keep the ratio the same if there are multiple items with the same code_e (item 1: quantity 1, item 2: quantity 2) if pasted leads to (item 1: quantity 2, item 2: quantity 4)

		jobItems.forEach((row) => {
			const { id, modified_at, created_at, fpo_id, ...filteredRow } = row;

			// If forceDuplicate is true (meta key pressed), always create new item
			if (forceDuplicate) {
				newJobItems.push({
					...filteredRow,
					id: uuidv4(),
					price: isNaN(filteredRow.price as number)
						? 0
						: filteredRow.price,
					job_document_id: jobDocumentId,
					discount: (filteredRow.discount as number) ?? 0,
					created_at: new Date().toISOString(),
					modified_at: new Date().toISOString(),
					fpo_id: null,
				});
			} else {
				// Check if item with same code_e exists that hasn't been matched yet
				const existingItem = existingItems.find(
					(item) =>
						item.code_e === filteredRow.code_e &&
						!matchedIds.has(item.id)
				);

				if (existingItem) {
					// Item exists and hasn't been matched yet, increase quantity
					matchedIds.add(existingItem.id);
					itemsToUpdate.push({
						id: existingItem.id,
						quantityToAdd: Number(filteredRow.quantity) || 1,
					});
				} else {
					// Item doesn't exist or all items with this code_e have been matched, create new one
					newJobItems.push({
						...filteredRow,
						id: uuidv4(),
						price: isNaN(filteredRow.price as number)
							? 0
							: filteredRow.price,
						job_document_id: jobDocumentId,
						discount: (filteredRow.discount as number) ?? 0,
						created_at: new Date().toISOString(),
						modified_at: new Date().toISOString(),
						fpo_id: null,
					});
				}
			}
		});

		set(
			produce((state) => {
				if (!state.jobItemsForDocuments[jobDocumentId]) {
					state.jobItemsForDocuments[jobDocumentId] = [];
				}

				// Add new items
				if (newJobItems.length > 0) {
					state.jobItemsForDocuments[jobDocumentId].push(
						...newJobItems
					);
				}

				// Update quantities for existing items
				itemsToUpdate.forEach(({ id, quantityToAdd }) => {
					const item = state.jobItemsForDocuments[jobDocumentId].find(
						(i: JobItemEntityType) => i.id === id
					);
					if (item) {
						item.quantity =
							Number(item.quantity || 0) + quantityToAdd;
					}
				});
			})
		);

		get().upsertJobItems(jobDocumentId, "Error pasting job items");
		get().pendingChangesNotification();
	},

	changeJobItem: async (jobItem: any, field?: string) => {
		const {
			SELECTED_FIELD,
			interpr_de,
			interpr_fr,
			interpr_it,
			is_new,
			custom,
			expanded,
			inEdit,
			selected,
			bpo_id,
			fpo_id,
			...filteredRow
		} = jobItem;

		// Store the current state for rollback
		const previousJobItems = [
			...get().jobItemsForDocuments[jobItem.job_document_id],
		];
		let error = false;

		const newCode = filteredRow.code;

		// If the field that was changed is the code, we need to consider
		// updating the whole row to a new tariff or article
		// To do this, we treat the entered code as code_e (code for entering a new position)
		if (field === "code") {
			const { error: changeError, message: changeErrorMessage } =
				await get().changeTariffInRow(newCode, jobItem);
			if (changeError) {
				Logger.error(
					`changeError: ${changeError} ${changeErrorMessage}`
				);
				error = true;
			}
		} else {
			const newJobItems = previousJobItems.map(
				(row: JobItemEntityType) =>
					row.id === filteredRow.id ? filteredRow : row
			);

			set(
				produce((state) => {
					state.jobItemsForDocuments[jobItem.job_document_id] =
						newJobItems;
				})
			);

			const { error: changeError } = await supabase
				.from(SupabaseTableEnum.JOB_ITEMS)
				.upsert([filteredRow], {
					onConflict: "id",
					defaultToNull: false,
				});

			if (changeError) {
				error = true;
				showNotification({
					message: "Fehler beim Ändern der Position",
					type: "error",
				});
			}
		}

		if (error) {
			// Rollback to previous state
			set(
				produce((state) => {
					state.jobItemsForDocuments[jobItem.job_document_id] =
						previousJobItems;
				})
			);

			Logger.error(error);
			return;
		}

		get().pushToHistory(jobItem.job_document_id, previousJobItems);
		get().pendingChangesNotification();
	},

	bulkChangeJobItems: async (
		jobItems: JobItemEntityType[],
		jobDocumentId: number
	) => {
		if (jobItems.length === 0) return;

		const previousJobItems = [...get().jobItemsForDocuments[jobDocumentId]];
		let error = false;

		const filteredItems = jobItems.map((jobItem) => {
			const {
				SELECTED_FIELD,
				interpr_de,
				interpr_fr,
				interpr_it,
				is_new,
				custom,
				expanded,
				inEdit,
				selected,
				bpo_id,
				fpo_id,
				...filteredRow
			} = jobItem as any;
			return filteredRow;
		});

		const updatedJobItems = previousJobItems.map(
			(row: JobItemEntityType) => {
				const update = filteredItems.find((item) => item.id === row.id);
				return update || row;
			}
		);

		set(
			produce((state) => {
				state.jobItemsForDocuments[jobDocumentId] = updatedJobItems;
			})
		);

		const { error: updateError } = await supabase
			.from(SupabaseTableEnum.JOB_ITEMS)
			.upsert(filteredItems, {
				onConflict: "id",
				defaultToNull: false,
			});

		if (updateError) {
			error = true;
			showNotification({
				message: "Fehler beim Ändern der Positionen",
				type: "error",
			});
		}

		if (error) {
			set(
				produce((state) => {
					state.jobItemsForDocuments[jobDocumentId] =
						previousJobItems;
				})
			);
			Logger.error(error);
			return;
		}

		get().pushToHistory(jobDocumentId, previousJobItems);
		get().pendingChangesNotification();
	},

	//updateJobItems: async (jobId: number, tp_value?: number, tp_variation?: number | null, price_list_id?: string) => {
	updateJobItems: async (
		jobId: number,
		tp_value?: number,
		tp_variation?: number
	) => {
		if (!jobId) {
			Logger.error("updateJobItems called with null/undefined jobId");
			return;
		}

		const { data, error: jobDocumentError } = await supabase
			.from(SupabaseTableEnum.JOB_DOCUMENTS)
			.select("id")
			.eq("job_id", jobId)
			.eq("status", JobStatusEnum.IN_PROGRESS);

		if (jobDocumentError || !data) {
			Logger.error(jobDocumentError);
			showNotification({
				message: "Fehler beim Aktualisieren des Auftrags",
				type: "error",
			});
			return;
		}

		// Get job details and tariff data if we need to recalculate prices
		const job = get().job;
		const tariffs =
			tp_variation !== undefined
				? useCatalogsStore.getState().tariffsLookup
				: null;

		// Check for critical missing data before processing
		if (tp_variation !== undefined) {
			if (!tariffs) {
				Logger.error(
					"[updateJobItems] Tariffs not available for price recalculation"
				);
				showNotification({
					message: "Fehler: Tarife konnten nicht geladen werden",
					type: "error",
				});
				return;
			}
			if (!job) {
				Logger.error(
					"[updateJobItems] Job not available for price recalculation"
				);
				showNotification({
					message: "Fehler: Auftragsdaten nicht verfügbar",
					type: "error",
				});
				return;
			}
			if (!job.price_list_id) {
				Logger.error(
					"[updateJobItems] Job price list id not available for price recalculation"
				);
				get().requireJobPricing("price-recalculation");
				return;
			}
		}

		// Optimistic update
		for (const item of data) {
			const jobDocumentId = item.id;
			const currentJobItems = get().jobItemsForDocuments[jobDocumentId];

			if (!currentJobItems) continue;

			let failedItemsCount = 0;

			const updatedJobItems = currentJobItems.map((jobItem) => {
				let updatedItem = { ...jobItem };

				// Update tp_value for tariff items
				if (
					tp_value !== undefined &&
					jobItem.type === JobItemTypeEnum.TARIFF
				) {
					updatedItem.tp_value = tp_value;
				}

				// Recalculate price if tp_variation changed
				if (
					tp_variation !== undefined &&
					jobItem.type === JobItemTypeEnum.TARIFF &&
					tariffs &&
					job &&
					job.price_list_id
				) {
					// Look up tariff by code_e (tariffs are indexed by code_e)
					const tariffsForCode = tariffs[jobItem.code_e];
					const tariffData = tariffsForCode
						? Object.values(tariffsForCode)[0]
						: undefined;
					if (tariffData) {
						updatedItem.price = calculateTariffPrice(
							tariffData,
							tp_variation,
							job.price_list_id
						);
					} else {
						Logger.warn(
							`[updateJobItems] Tariff data not found for code_e: ${jobItem.code_e}`
						);
						failedItemsCount++;
					}
				}

				return updatedItem;
			});

			// Notify if some items failed to update
			if (failedItemsCount > 0) {
				showNotification({
					message: `${failedItemsCount} Position${
						failedItemsCount > 1 ? "en" : ""
					} konnte${
						failedItemsCount > 1 ? "n" : ""
					} nicht aktualisiert werden`,
					type: "warning",
				});
			}

			// Update local state
			set(
				produce((state) => {
					state.jobItemsForDocuments[jobDocumentId] = updatedJobItems;
				})
			);

			// Use the batch upsert function to update all items at once
			await get().upsertJobItems(
				jobDocumentId,
				"Fehler beim Aktualisieren der Positionen"
			);
		}
	},
	changeTariffInRow: async (code: string, jobItem: JobItemEntityType) => {
		const jobItems = get().jobItemsForDocuments[jobItem.job_document_id];
		const job = get().job;

		if (!job) {
			showNotification({
				message: "Es wurde kein Auftrag gefunden",
				type: "error",
			});
			return { error: true, message: "Es wurde kein Auftrag gefunden" };
		}
		const pricing = get().requireJobPricing("item-insert");
		if (!pricing) {
			return {
				error: true,
				message: jobPricingProblemsMessage(getJobPricingProblems(job)),
			};
		}
		const jobTpVariation = job.tp_variation;

		let type;

		// Check if tariffs are still loading when trying to change a tariff
		const catalogsState = useCatalogsStore.getState();
		if (catalogsState.isLoadingTariffs) {
			showNotification({
				message:
					"Leistungen werden noch geladen. Bitte versuchen Sie es in wenigen Sekunden erneut.",
				type: "info",
			});
			return { error: true, message: "Tariffs are still loading" };
		}

		let newJobItem:
			| ExtendedArticleEntityType
			| TariffWithPricesViewType
			| undefined;

		// Check if it's a tariff
		const tariffsForCode = catalogsState.tariffsLookup[code];
		if (tariffsForCode && Object.keys(tariffsForCode).length > 0) {
			let itemsToConsider = Object.values(tariffsForCode);

			// If there are exactly 2 tariffs and one is custom and one is default, prefer the custom one
			if (
				itemsToConsider.length === 2 &&
				itemsToConsider.some((item) => item.is_custom) &&
				itemsToConsider.some((item) => !item.is_custom)
			) {
				itemsToConsider = itemsToConsider.filter(
					(item) => item.is_custom
				);
			}

			newJobItem = itemsToConsider[0];
			type = JobItemTypeEnum.TARIFF;
		}

		if (!newJobItem) {
			// Check if it's an article
			const articlesForCode = useCentralStore.getState().articles[code];
			if (articlesForCode) {
				newJobItem = Object.values(articlesForCode)[0];
				type = JobItemTypeEnum.ARTICLE_MATERIAL;
			}
		}

		if (!newJobItem) {
			showNotification({
				message: "Position konnte nicht gefunden werden",
				type: "error",
			});
			return {
				error: true,
				message: "Position konnte nicht gefunden werden",
			};
		}
		const newFormattedJobItem =
			type != JobItemTypeEnum.TARIFF
				? formatArticle(
						newJobItem as ExtendedArticleEntityType,
						jobItem.job_document_id
					)
				: tariffToJobItem(
						newJobItem as TariffWithPricesViewType,
						jobItem.job_document_id,
						pricing.priceListId,
						pricing.tpValue,
						jobTpVariation ?? 0
					);

		if (newFormattedJobItem.error) {
			showNotification({
				message: newFormattedJobItem.error,
				type: "error",
			});
			return { error: true, message: newFormattedJobItem.error };
		}

		const newFormattedJobItemData = newFormattedJobItem.data;

		const changedJobItem = {
			...newFormattedJobItemData,
			techn_perc_2: jobItem?.techn_perc_2,
			techn_perc_3: jobItem?.techn_perc_3,
			profile_id: jobItem?.profile_id,
			profile2_id: jobItem?.profile2_id,
			profile3_id: jobItem?.profile3_id,
			id: jobItem.id,
			job_document_id: jobItem.job_document_id,
			quantity: jobItem.quantity,
		};

		const newJobItems = jobItems.map((item, i) =>
			item.id === jobItem.id ? changedJobItem : item
		);

		set(
			produce((state) => {
				state.jobItemsForDocuments[jobItem.job_document_id] =
					newJobItems;
			})
		);

		get().upsertJobItems(
			jobItem.job_document_id,
			"Error changing position"
		);
		get().pendingChangesNotification();

		return { error: false, message: "Position erfolgreich geändert" };
	},
	handleItemSelection: (
		item: TariffWithPricesViewType | ExtendedArticleEntityType | null
	) => {
		const dialog = get().itemSelectionDialog;
		if (dialog?.resolve) {
			dialog.resolve(item);
		}
		set({
			itemSelectionDialog: null,
		});
	},
});
